import clsx from 'clsx'
import { Typography } from '../common/typography'
import { useMemo, useState } from 'react'
import Tabs from '../stories/Tabs/Tabs'
import { Course, useLearningStats, useCourses } from './api'
import { Table } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import {
  IconZoom,
  IconCircleCheck,
  IconDotsCircleHorizontal,
  IconCircle,
  IconCircleX,
} from '@tabler/icons-react'
import classes from './Learning.module.css'
import { useNavigate } from 'react-router'
import LearningLevel from './components/LearningLevel'
import LearningProgress from './components/LearningProgress'
import LearningStats from './components/LearningStats'
import { Input } from '../common/components/input/StyledTextInput'

export default function MyLearning() {
  const navigate = useNavigate()
  const tabs = [
    {
      value: 'all',
      label: 'All',
    },
    {
      value: 'completed',
      label: 'Completed',
    },
    {
      value: 'in_progress',
      label: 'In Progress',
    },
    {
      value: 'not_started',
      label: 'Not Started',
    },
  ]
  const [activeTab, setActiveTab] = useState<string>('all')

  const { data: learningStats } = useLearningStats()
  const { data: courses } = useCourses()
  const isMobile = useMediaQuery('(max-width: 800px)')
  const [searchValue, setSearchValue] = useState<string>('')

  const totalCourses =
    learningStats.completed_courses +
    learningStats.in_progress_courses +
    learningStats.not_started_courses

  const completedPercent =
    totalCourses > 0
      ? (learningStats.completed_courses / totalCourses) * 100
      : 0
  const inProgressPercent =
    totalCourses > 0
      ? (learningStats.in_progress_courses / totalCourses) * 100
      : 0
  const notStartedPercent =
    totalCourses > 0
      ? (learningStats.not_started_courses / totalCourses) * 100
      : 0

  const filteredCourses = useMemo(() => {
    let result = courses.filter((course) => course.is_published)

    if (activeTab !== 'all') {
      if (activeTab === 'completed') {
        result = result.filter(
          (course) =>
            course.status === 'completed' || course.status === 'failed'
        )
      } else {
        result = result.filter((course) => course.status === activeTab)
      }
    }

    if (searchValue.trim()) {
      result = result.filter((course) =>
        course.title.toLowerCase().includes(searchValue.toLowerCase())
      )
    }

    return result
  }, [courses, activeTab, searchValue])

  const rows = filteredCourses.map((course: Course) => {
    const StatusIcon =
      course.status === 'completed'
        ? IconCircleCheck
        : course.status === 'in_progress'
          ? IconDotsCircleHorizontal
          : course.status === 'failed'
            ? IconCircleX
            : IconCircle

    const statusIconClass =
      course.status === 'completed'
        ? classes.statusIconCompleted
        : course.status === 'in_progress'
          ? classes.statusIconInProgress
          : course.status === 'failed'
            ? classes.statusFailed
            : classes.statusIconNotStarted

    return (
      <Table.Tr
        key={course.quiz_id}
        onClick={() => navigate(`/learning/course/${course.quiz_id}`)}
        style={{ cursor: 'pointer' }}
      >
        <Table.Td title={course.title}>
          <Typography lh="150">{course.title}</Typography>
        </Table.Td>
        <Table.Td>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <StatusIcon className={statusIconClass} />
          </div>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {course.xp_earned ?? '-'}
          </Typography>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="140" ta="center">
            {course.duration_seconds
              ? dayjs()
                  .startOf('day')
                  .add(course.duration_seconds, 'seconds')
                  .format('mm:ss')
              : '-'}
          </Typography>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {course.completed_at
              ? dayjs(course.completed_at).format('DD/MM/YYYY')
              : '-'}
          </Typography>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {course.soft_deadline
              ? dayjs(course.soft_deadline).format('DD/MM/YYYY')
              : '-'}
          </Typography>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {course.hard_deadline
              ? dayjs(course.hard_deadline).format('DD/MM/YYYY')
              : '-'}
          </Typography>
        </Table.Td>
        <Table.Td>
          {course.quiz_retry_attempts === 0 ? (
            <Typography lh="150" ta="center">
              Unlimited
            </Typography>
          ) : (
            <Typography ff="JetBrains Mono" lh="150" ta="center">
              {course.quiz_retry_attempts}
            </Typography>
          )}
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <div className={classes.learningContainer}>
      <div className={classes.learningStatsContainer} data-mobile={isMobile}>
        <div className={classes.learningTopRow}>
          <div className={classes.levelWrapper}>
            <LearningLevel level={learningStats.xp_level} />
          </div>
          <div className={classes.progressWrapper}>
            <LearningProgress
              completedPercent={completedPercent}
              inProgressPercent={inProgressPercent}
              notStartedPercent={notStartedPercent}
            />
          </div>
        </div>
        <div className={classes.statsWrapper}>
          <LearningStats
            xpRating={learningStats.xp_rating}
            xpPoints={learningStats.xp_points}
            rank={learningStats.rank}
          />
        </div>
      </div>

      <div className={classes.quizzesContainer}>
        <div className={classes.quizzesHeader} data-mobile={isMobile}>
          <Tabs items={tabs} value={activeTab} onChange={setActiveTab} />
          <Input
            placeholder="Search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.currentTarget.value)}
            rightSection={<IconZoom className={classes.searchIcon} />}
            rightSectionPointerEvents="none"
            styles={{ root: { maxWidth: '15rem' } }}
          />
        </div>

        <div className={classes.tableContainer}>
          <Table.ScrollContainer minWidth={300}>
            <Table
              classNames={{
                table: clsx(
                  classes.table,
                  isMobile && classes.stickyFirstColumn
                ),
                tr: classes.tr,
                th: classes.th,
                td: classes.td,
              }}
              verticalSpacing="xs"
              withRowBorders={false}
            >
              <Table.Thead>
                <Table.Tr className={classes.headerRow}>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                    >
                      Course
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                      ta="center"
                    >
                      Status
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                      ta="center"
                    >
                      XP Earn
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                      ta="center"
                    >
                      Duration
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                      ta="center"
                    >
                      Completion Date
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                      ta="center"
                    >
                      Soft Deadline
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                      ta="center"
                    >
                      Hard Deadline
                    </Typography>
                  </Table.Th>
                  <Table.Th>
                    <Typography
                      fs="sm"
                      fw={600}
                      fc={{ light: 'gray-9', dark: 'gray-9' }}
                      lh="170"
                      ta="center"
                    >
                      Quiz Attempts
                    </Typography>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredCourses.length > 0 ? (
                  rows
                ) : searchValue ? (
                  <Table.Tr>
                    <Table.Td colSpan={8}>
                      <Typography ta="center">
                        No courses found matching "{searchValue}"
                      </Typography>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </div>
      </div>
    </div>
  )
}
